function asStr(v) {
  return String(v ?? "").trim();
}

/** snake_case / mixed IDs → camelCase suffix for i18n keys (after_session → afterSession). */
export function referenceListIdToCamelCase(id) {
  const s = asStr(id);
  if (!s) return "";
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * @typedef {{ id: string, builtin?: boolean, enabled?: boolean, sort?: number, label?: string, isDefault?: boolean }} ReferenceListEntry
 */

/**
 * @param {readonly string[]} ids
 * @returns {ReferenceListEntry[]}
 */
export function buildDefaultReferenceListEntries(ids) {
  return ids.map((id, index) => ({
    id,
    builtin: true,
    enabled: true,
    sort: (index + 1) * 10,
    isDefault: index === 0,
  }));
}

/**
 * @param {unknown} rawList
 * @returns {ReferenceListEntry[]|null}
 */
export function normalizeReferenceListEntriesArray(rawList) {
  if (!Array.isArray(rawList)) return null;
  return rawList
    .filter((row) => row && typeof row === "object" && asStr(row.id))
    .map((row) => ({
      id: asStr(row.id),
      builtin: row.builtin === true,
      enabled: row.enabled !== false,
      sort: Number(row.sort) || 0,
      label: asStr(row.label) || undefined,
      isDefault: row.isDefault === true,
    }));
}

/**
 * @param {unknown} configured
 * @param {ReferenceListEntry[]} defaults
 * @param {{ adminView?: boolean }} [options]
 * @returns {ReferenceListEntry[]}
 */
export function mergeReferenceListEntries(configured, defaults, { adminView = false } = {}) {
  const defaultsById = new Map(defaults.map((row) => [row.id, row]));
  const out = [];
  const seen = new Set();

  if (Array.isArray(configured) && configured.length) {
    for (const raw of configured) {
      if (!raw || typeof raw !== "object") continue;
      const id = asStr(raw.id);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const fallback = defaultsById.get(id);
      out.push({
        id,
        builtin: raw.builtin === true || Boolean(fallback),
        enabled: raw.enabled !== false,
        sort: Number(raw.sort) || fallback?.sort || (out.length + 1) * 10,
        label: asStr(raw.label) || undefined,
        isDefault: raw.isDefault === true,
      });
    }
  }

  for (const fallback of defaults) {
    if (!seen.has(fallback.id)) out.push({ ...fallback });
  }

  const sorted = out.sort(
    (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || asStr(a.id).localeCompare(asStr(b.id))
  );

  const withDefaults = normalizeReferenceListDefaultFlags(sorted);
  if (adminView) return withDefaults;
  return withDefaults.filter((row) => row.enabled !== false);
}

/**
 * Stellt sicher: genau ein aktiver Standard; fehlt einer, wird der erste aktive Eintrag Standard.
 *
 * @param {ReferenceListEntry[]} rows
 * @param {{ trackDefault?: boolean }} [options]
 * @returns {ReferenceListEntry[]}
 */
export function normalizeReferenceListDefaultFlags(rows, { trackDefault = true } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!trackDefault || !list.length) {
    return list.map((row) => ({ ...row, isDefault: false }));
  }

  const sortRows = (rows) =>
    [...rows].sort(
      (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || asStr(a.id).localeCompare(asStr(b.id))
    );

  const enabledRows = sortRows(list.filter((row) => row?.enabled !== false));
  if (!enabledRows.length) {
    return list.map((row) => ({ ...row, isDefault: false }));
  }

  const explicit = sortRows(list.filter((row) => row?.isDefault === true && row?.enabled !== false));
  const defaultId = explicit.length ? explicit[0].id : enabledRows[0].id;

  return list.map((row) => ({
    ...row,
    isDefault: row.id === defaultId && row.enabled !== false,
  }));
}

/**
 * @param {ReferenceListEntry[]} rows
 * @returns {string}
 */
export function resolveReferenceListDefaultId(rows) {
  const normalized = normalizeReferenceListDefaultFlags(rows);
  return normalized.find((row) => row.isDefault === true)?.id || "";
}

/**
 * Markiert einen Eintrag als Standard und verschiebt ihn an Position 1.
 *
 * @param {ReferenceListEntry[]} rows
 * @param {string} selectedId
 * @returns {ReferenceListEntry[]}
 */
export function applyReferenceListDefaultSelection(rows, selectedId) {
  const id = asStr(selectedId);
  if (!id) return rows;
  const idx = (Array.isArray(rows) ? rows : []).findIndex((row) => row?.id === id);
  if (idx < 0) return rows;

  const flagged = normalizeReferenceListDefaultFlags(
    rows.map((row) => ({
      ...row,
      isDefault: row.id === id,
    }))
  );

  const selectedIdx = flagged.findIndex((row) => row.id === id);
  if (selectedIdx <= 0) {
    return flagged.map((row, index) => ({ ...row, sort: (index + 1) * 10 }));
  }

  const copy = [...flagged];
  const [item] = copy.splice(selectedIdx, 1);
  copy.unshift(item);
  return copy.map((row, index) => ({ ...row, sort: (index + 1) * 10 }));
}

/**
 * @param {ReferenceListEntry} entry
 * @param {(key: string) => string} t
 * @param {(id: string) => string} labelKeyForId
 */
export function resolveReferenceListLabel(entry, t, labelKeyForId) {
  const custom = asStr(entry?.label);
  if (custom) return custom;
  const id = asStr(entry?.id);
  if (!id) return "";
  const key = typeof labelKeyForId === "function" ? labelKeyForId(id) : "";
  if (key && typeof t === "function") return t(key);
  return id;
}

/**
 * @param {ReferenceListEntry[]} rows
 */
export function serializeReferenceListEntries(rows, { trackDefault = false } = {}) {
  const normalized = trackDefault ? normalizeReferenceListDefaultFlags(rows) : rows;
  return normalized.map((row, index) => {
    /** @type {ReferenceListEntry} */
    const out = {
      id: row.id,
      builtin: row.builtin === true,
      enabled: row.enabled !== false,
      sort: Number(row.sort) || (index + 1) * 10,
      label: asStr(row.label) || undefined,
    };
    if (trackDefault && row.isDefault === true) out.isDefault = true;
    return out;
  });
}
